import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { Brand, Platform } from '@fametrue/shared';

export const PLATFORM_NAMES: Record<string, string> = {
  trustpilot: 'Trustpilot',
  google: 'Google',
  feedaty: 'Feedaty',
  'recensioni-verificate': 'Recensioni Verificate',
  trustmate: 'TrustMate',
  'trusted-shops': 'Trusted Shops',
  glassdoor: 'Glassdoor',
};

export function platformName(p: Pick<Platform, 'id' | 'name'>): string {
  return PLATFORM_NAMES[p.id] ?? p.name;
}

/** Piattaforme analizzate: le non disponibili sono escluse dal rendering. */
export function availablePlatforms(brand: Brand): Platform[] {
  return brand.platforms.filter((p) => p.available !== false);
}

/**
 * Piattaforme che alimentano la card "Segnali da altre piattaforme".
 *
 * Trustpilot e Google possono avere un blocco `intelligence`, ma il loro
 * contributo è già nelle card 01-04 (spec §7.5). La card esiste solo se resta
 * almeno una piattaforma: il predicato sta qui perché lo usano sia l'elenco
 * delle card sia il corpo che le rende.
 */
export function extraIntelligencePlatforms(brand: Brand): Platform[] {
  return availablePlatforms(brand).filter(
    (p) => p.id !== 'trustpilot' && p.id !== 'google' && p.intelligence,
  );
}

/**
 * Griglia delle card piattaforma (Level 2).
 *
 * Ordine fisso: prima Trustpilot, poi Google, poi le altre. Si rendono solo
 * le piattaforme disponibili: il riferimento riempie la griglia fino a 4 slot
 * con card vuote "Nessuna Piattaforma" (`platformCards()`, spec §4), ma su
 * richiesta del committente quelle card non si mostrano più. Divergenza
 * deliberata dal v40: con meno di 4 piattaforme la griglia è più corta.
 *
 * Card ridotta [v2]: solo nome, score e distribuzione stelle. `basicLinkRate` e
 * `intelligence` restano nel JSON ma NON si renderizzano qui, e la parola
 * "BasicLink" non compare mai in interfaccia. Il riferimento ha ancora le
 * funzioni `basicLinkBadge` e `basicLinkClass`, ma sono codice morto: nessuno
 * le chiama. Non vanno "recuperate".
 */
export function PlatformCards({ brand }: { brand: Brand }): ReactNode {
  const platforms = availablePlatforms(brand);

  const trustpilot = platforms.find((p) => p.id === 'trustpilot') ?? null;
  const google = platforms.find((p) => p.id === 'google') ?? null;
  const others = platforms.filter((p) => p.id !== 'trustpilot' && p.id !== 'google');

  const ordered = [trustpilot, google, ...others].filter((p): p is Platform => p !== null);

  return (
    <>
      {ordered.map((p) => (
        <PlatformCard key={p.id} platform={p} />
      ))}
    </>
  );
}

function PlatformCard({ platform }: { platform: Platform }): ReactNode {
  const { t } = useTranslation();

  return (
    <div className="platform-card">
      <div className="platform-header">
        <span className="platform-name">{platformName(platform)}</span>
        <span className="platform-rating">
          {platform.rating ?? '—'}
          <span className="platform-rating-scale">{platform.rating != null ? ' / 5' : ''}</span>
        </span>
      </div>
      <div className="platform-reviews">
        {platform.reviews
          ? `${platform.reviews.toLocaleString('it-IT')} ${t('decision.reviews')}`
          : ''}
      </div>
      <StarBars stars={platform.stars} />
    </div>
  );
}

/** Distribuzione stelle, da 5 a 1, con la percentuale a una cifra decimale. */
function StarBars({ stars }: { stars: Platform['stars'] }): ReactNode {
  const { t } = useTranslation();
  const values = stars as Record<string, number | undefined>;

  if (Object.keys(values).length === 0) {
    return (
      <div style={{ color: '#9B9590', fontSize: 12, fontStyle: 'italic', padding: '12px 0' }}>
        {t('decision.noData')}
      </div>
    );
  }

  return (
    <div className="star-bars">
      {[5, 4, 3, 2, 1].map((i) => {
        const pct = values[String(i)] ?? 0;
        return (
          <div className="star-bar-row" key={i}>
            <span className="star-bar-label">{i}★</span>
            <div className="star-bar-track">
              <div className={`star-bar-fill s${i}`} style={{ width: `${pct}%` }} />
            </div>
            <span className="star-bar-pct">{pct.toFixed(1)}%</span>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Pannello `intelligence` di una piattaforma, usato SOLO dal blocco 05.
 * La struttura è schema-less: si mostrano i campi noti se presenti, e i temi
 * negativi come pillole. Nulla è obbligatorio.
 */
export function PlatformIntelligence({
  intelligence,
}: {
  intelligence: Record<string, unknown>;
}): ReactNode {
  const { t } = useTranslation();

  const rows: { key: string; label: string; value: string; danger?: boolean }[] = [];
  const asString = (v: unknown): string | null => (typeof v === 'string' && v ? v : null);

  // Chiavi introdotte dal v40, rese PRIMA di quelle già presenti: l'ordine è
  // quello del riferimento, non alfabetico.
  const NEW_KEYS = [
    ['poolProprio', 'poolProprio'],
    ['valutazioneProdotto', 'valutazioneProdotto'],
    ['scoreEsposto', 'scoreEsposto'],
    ['riconciliazione', 'riconciliazione'],
    ['avvioRaccolta', 'avvioRaccolta'],
    ['presidio', 'presidio'],
  ] as const;

  for (const [field, key] of NEW_KEYS) {
    const value = asString(intelligence[field]);
    if (value) rows.push({ key, label: t(`decision.intel.${key}`), value });
  }

  const asse = asString(intelligence['asse']);
  if (asse) rows.push({ key: 'asse', label: t('decision.intel.asse'), value: asse });

  const genuino = asString(intelligence['genuinoAttesoVerticale']);
  if (genuino) rows.push({ key: 'genuino', label: t('decision.intel.genuino'), value: genuino });

  const pattern = asString(intelligence['patternTesti']);
  if (pattern) rows.push({ key: 'pattern', label: t('decision.intel.pattern'), value: pattern });

  const deterioramento = asString(intelligence['deterioramentoDatato']);
  if (deterioramento) {
    rows.push({
      key: 'deterioramento',
      label: t('decision.intel.deterioramento'),
      value: deterioramento,
    });
  }

  const flag = asString(intelligence['flag']);
  // "nessuno" è il valore che indica assenza di flag: non va mostrato.
  if (flag && flag !== 'nessuno') {
    rows.push({ key: 'flag', label: t('decision.intel.flag'), value: flag, danger: true });
  }

  // v40: nota discorsiva fra le righe e i temi negativi. Arriva dal JSON come
  // HTML, come già `analysis` (spec §6), e si rende tale e quale.
  const nota = asString(intelligence['nota']);

  const temiRaw = intelligence['temiNegativi'];
  const temi =
    temiRaw && typeof temiRaw === 'object'
      ? Object.entries(temiRaw as Record<string, unknown>).filter(
          (entry): entry is [string, number] => typeof entry[1] === 'number',
        )
      : [];

  if (rows.length === 0 && temi.length === 0 && !nota) return null;

  return (
    <div className="platform-intel">
      <div className="pi-title">{t('decision.intel.title')}</div>
      {rows.map((row) => (
        <div className="pi-row" key={row.key}>
          <span className="pi-k">{row.label}</span>
          <span className="pi-v" style={row.danger ? { color: 'var(--red-dark)' } : undefined}>
            {row.value}
          </span>
        </div>
      ))}
      {nota ? <div style={{ marginTop: 14 }} dangerouslySetInnerHTML={{ __html: nota }} /> : null}
      {temi.length > 0 ? (
        <div className="pi-temi">
          {temi.map(([tema, pct]) => (
            <span className="pi-tema" key={tema}>
              {tema.replace(/_/g, ' ')} <b>{pct}%</b>
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}
